import type { Language } from '@eformulieren/engine'
import type { SourceId } from '@eformulieren/model'
import { type ReactNode, useCallback, useRef, useState } from 'react'
import { sourcesOf, addSource } from '../document/sectionDocument.js'
import { editSourceInLayer, layerLibrary, removeSourceInLayer } from './layerSources.js'
import type * as Y from 'yjs'
import { inLanguage } from '../language/text.js'
import { SourcePanel } from './SourcePanel.js'

/**
 * De echte `chooseReference` van `Richtext.tsx`, en het paneel dat erbij hoort (R-50).
 *
 * Eén kiezer-instantie per sectie en niet per rich-text-veld: de bronnenopslag hoort bij de
 * sectie (`sectiedocument.ts`, `SOURCES`), niet bij één veld, en meerdere rich-text-velden in
 * dezelfde sectie delen dus dezelfde `Y.Doc`-lezing. **Welk paneel zichtbaar is, is wel per veld
 * en niet per sectie**: staan er twee rich-text-velden naast elkaar in dezelfde sectie, dan mag
 * een klik in het ene veld niet ook het paneel onder het andere laten verschijnen. Vandaar dat
 * `chooseReferenceFor`/`panelFor` zowel een sectie-id als een veld-id nemen: de sectie-id wijst
 * de `Y.Doc` aan, de veld-id bepaalt zichtbaarheid.
 *
 * **Eén open kiezer tegelijk, over alle secties en velden heen.** Een tweede aanroep terwijl de
 * eerste nog wacht overschrijft hem: de eerste `Promise` lost dan nooit op. Dat kan in de
 * praktijk niet gebeuren zolang de knop die hem aanroept een `<button>` is en de gebruiker maar
 * één ding tegelijk aanklikt.
 *
 * **Eén bibliotheek per laag, zoals CitatenPOC er één per formulier heeft** (R-50). Het paneel biedt
 * de bronnen van alle secties van dezelfde laag in de projectie (`scopeOf`), zodat hetzelfde boek in
 * twee secties één bron blijft en één regel in de literatuurlijst. Een gekozen bron uit een andere
 * sectie wordt in de eigen sectie gezet: de documenten blijven per sectie (B6), en wie alleen deze
 * sectie ziet, ziet zo ook haar bron. Zonder `scopeOf` alleen de eigen sectie.
 */
export interface SourcePicker {
  chooseReferenceFor(sectionId: string, fieldId: string): (options?: ChooseOptions) => Promise<readonly string[] | undefined>
  panelFor(sectionId: string, fieldId: string): ReactNode
}

/**
 * Hoe de kiezer opent: met `@` in de tekst (R-14, CitatenPOC). De focus staat in de dialoog altijd in
 * het zoekveld, dus de kiezer hoeft dit niet meer te weten; het blijft in de handtekening omdat
 * `Richtext` zegt hoe hij opende.
 */
export interface ChooseOptions {
  readonly zoeken?: boolean
}

/**
 * Wat de kiezer nodig heeft om de bronnen te beheren (opdracht van 30 oktober 2026, R-50).
 *
 * Een gekozen bron staat als kopie in elke sectie die hem citeert (zie boven), dus wijzigen en
 * verwijderen gaan over alle kopieën in de laag. Een lezer kan niet in elke sectie schrijven (een
 * medeschrijver via een deling, R-65): een schrijfactie daar weigert de pod, en de kopie zou stil
 * achterblijven. Daarom alleen de kopieën in een sectie die hij kan bewerken (`canWrite`), en een
 * zin in het paneel als er een buiten bereik bleef. Verwijderen kan alleen bij een bron die nergens
 * in de tekst van de laag staat (`inUse`), zodat geen citatie naar niets gaat wijzen.
 */
export interface ManageOptions {
  readonly inUse?: (sectionId: string) => ReadonlySet<string>
  readonly canWrite?: (sectionId: string) => boolean
}

const PARTLY_CHANGED = {
  nl: 'Gewijzigd. In een sectie die u niet kunt bewerken blijft de oude beschrijving staan.',
  en: 'Changed. In a section you cannot edit, the old description stays.',
}

export function useSourcePicker(
  documents: ReadonlyMap<string, Y.Doc>,
  language: Language,
  newSourceId: () => string,
  scopeOf: (sectionId: string) => readonly string[] = (sectionId) => [sectionId],
  { inUse, canWrite = () => true }: ManageOptions = {},
): SourcePicker {
  const [open, setOpen] = useState<{
    readonly sectieId: string
    readonly veldId: string
    readonly resolve: (sources: readonly string[] | undefined) => void
  } | null>(null)
  const [notice, setNotice] = useState<string | undefined>(undefined)
  /**
   * De keuze, vastgehouden tot de dialoog dicht is. Pas dan lost de belofte op: zolang hij modaal
   * openstaat is de tekst inert, en de editor kan de bronvermelding pas daarna invoegen en de cursor
   * terugzetten op de plek in de tekst (`Dialog`, `returnFocus`).
   */
  const chosen = useRef<readonly string[] | undefined>(undefined)
  // Een wijziging in een document hertekent dit paneel niet vanzelf; deze teller wel.
  const [, setRevision] = useState(0)

  const chooseReferenceFor = useCallback(
    (sectionId: string, fieldId: string) =>
      (_options?: ChooseOptions): Promise<readonly string[] | undefined> =>
        new Promise((resolve) => {
          setNotice(undefined)
          chosen.current = undefined
          setOpen({ sectieId: sectionId, veldId: fieldId, resolve })
        }),
    [],
  )

  const panelFor = useCallback(
    (sectionId: string, fieldId: string): ReactNode => {
      if (open === null || open.sectieId !== sectionId || open.veldId !== fieldId) return null
      const doc = documents.get(sectionId)
      if (doc === undefined) return null
      // De eigen sectie eerst, dan de andere secties van de laag in formuliervolgorde; bij hetzelfde id telt de eerste.
      const scope = [...new Set([sectionId, ...scopeOf(sectionId)])]
      const library = layerLibrary(documents, scope)

      return (
        <SourcePanel
          sources={[...library.values()]}
          taal={language}
          returnFocus={() => open.resolve(chosen.current)}
          {...(inUse === undefined ? {} : { inUse: inUse(sectionId) })}
          {...(notice === undefined ? {} : { melding: notice })}
          onWijzig={(sourceId, changed) => {
            const { buitenBereik } = editSourceInLayer(documents, scope, canWrite, sourceId, changed)
            setNotice(buitenBereik ? inLanguage(PARTLY_CHANGED, language) : undefined)
            setRevision((count) => count + 1)
          }}
          onVerwijder={(sourceId) => {
            if (removeSourceInLayer(documents, scope, canWrite, sourceId, inUse?.(sectionId) ?? new Set())) setRevision((count) => count + 1)
          }}
          onKies={(existing, newSource) => {
            const own = new Set(sourcesOf(doc).map((source) => source.id))
            for (const id of existing) {
              const chosen = library.get(id)
              if (chosen !== undefined && !own.has(id)) addSource(doc, chosen)
            }
            const createNew = newSource === undefined ? undefined : newSourceId()
            if (newSource !== undefined && createNew !== undefined) {
              addSource(doc, { id: createNew as SourceId, ...newSource })
            }
            chosen.current = createNew === undefined ? existing : [...existing, createNew]
            setOpen(null)
          }}
          onCancel={() => {
            chosen.current = undefined
            setOpen(null)
          }}
        />
      )
    },
    [open, documents, language, newSourceId, scopeOf, inUse, canWrite, notice],
  )

  return { chooseReferenceFor, panelFor }
}
